import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import CertificationForm from '../forms/CertificationForm';
import useCertifications from '../../hooks/useCertifications';
import useProfile from '../../hooks/UseProfile';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import CertificationList from './CertificationList';
import { useQueryClient } from 'react-query';

const CertificationSection = ({ cv, compact = false, onAddClick, triggerAdd, readonly = false }) => {
    const queryClient = useQueryClient();
    const { certifications, addCertification, updateCertification, deleteCertification, isLoading } = useCertifications(cv?.id);
    const { refreshProfile } = useProfile();
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCertification, setSelectedCertification] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const prevTriggerRef = useRef(0);

    const handleAdd = () => {
        setSelectedCertification(null);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (triggerAdd && triggerAdd !== prevTriggerRef.current) {
            prevTriggerRef.current = triggerAdd;
            handleAdd();
        }
    }, [triggerAdd]);

    const handleEdit = (certification) => {
        setSelectedCertification(certification);
        setIsModalOpen(true);
    };

    const handleDelete = (certification) => {
        setSelectedCertification(certification);
        setIsDeleteModalOpen(true);
    };

    const handleSubmit = async (data) => {
        try {
            setError(null); // Clear any previous errors
            const dataToSave = cv?.id ? { ...data, cv: cv.id } : data;
            if (selectedCertification) {
                // updateCertification is now a React Query mutation
                await updateCertification({ ...dataToSave, id: selectedCertification.id });
            } else {
                // addCertification is now a React Query mutation
                await addCertification(dataToSave);
            }
            // Invalidate and refetch queries to ensure the list updates
            await queryClient.invalidateQueries(['certifications', cv?.id || null]);
            await queryClient.refetchQueries(['certifications', cv?.id || null]);
            await queryClient.invalidateQueries(['certifications']);
            // We still need to refresh the profile as it might contain aggregated data
            if (!cv) {
                await refreshProfile();
            }
            setIsModalOpen(false);
            setError(null); // Clear error on success
        } catch (err) {
            console.error('Error saving certification:', err);
            // Only show error if it's a real error, not a successful response
            const errorMessage = err?.message || err?.error || 'Failed to save certification';
            setError(errorMessage);
        }
    };

    const handleConfirmDelete = async () => {
        await queryClient.invalidateQueries(['certifications', cv?.id || null]);
        await queryClient.refetchQueries(['certifications', cv?.id || null]);
        await queryClient.invalidateQueries(['certifications']);
        setIsDeleteModalOpen(false);
    };

    const content = (
        <>
            {error && (
                <Typography color="error" className="mb-4">
                    {error}
                </Typography>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <Typography>Loading certifications...</Typography>
                </Box>
            ) : (
                <CertificationList 
                    certifications={certifications} 
                    onEdit={readonly ? undefined : handleEdit} 
                    onDelete={readonly ? undefined : handleDelete}
                    compact={compact}
                />
            )}

            <CustomModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                title={selectedCertification ? 'Edit Certification' : 'Add New Certification'}
            >
                <CertificationForm 
                    id={selectedCertification?.id}
                    onClose={() => setIsModalOpen(false)}
                    cvId={cv?.id}
                    onSubmit={handleSubmit}
                />
            </CustomModal>

            <DeleteDialog
                url={`/job/cv/certificate/${selectedCertification?.id}`}
                isOpen={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                notifyDone={handleConfirmDelete}
                message="Are you sure you want to delete this certification?"
            />
        </>
    );

    if (compact) {
        return content;
    }

    return (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Certifications
                </Typography>
                {!readonly && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                )}
            </Box>
            {content}
        </Paper>
    );
};

export default CertificationSection; 